'use client';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { DynamicPageData } from '@eliadmin/core/src/api/pages/pages-type';
import { ContactForm } from '@eliadmin/core/src/components/contact/contact-form';

import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdOutlineEmail, MdOutlinePhoneInTalk } from 'react-icons/md';

export function ContactPageHeader({
  page,
  generalLayout,
}: {
  page: DynamicPageData;
  generalLayout: GeneralLayout['data'];
}) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'generic-gap',
        'mb-8 py-8 sm:mb-10 sm:py-10 lg:mb-12 lg:py-12',
      )}
    >
      <div className={cn('custom-container', 'flex flex-col items-center justify-center gap-6')}>
        <Breadcrumbs
          breadcrumb={{ name: page?.name, slug: page?.slug }}
          className={{
            wrapper: 'flex w-full items-center justify-center',
            list: 'flex w-full items-center justify-center',
          }}
        />
        <h1 className={cn('h1', 'mb-8 text-center font-bold tracking-tight sm:mb-10 lg:mb-12')}>
          {page?.name}
        </h1>
      </div>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 rounded-[4px] bg-primary-foreground p-3 sm:gap-10 sm:p-6 md:flex-row md:items-stretch md:justify-between lg:gap-12 lg:p-8',
        )}
      >
        <div className="flex w-full flex-col items-start justify-between gap-8 md:max-w-[652px]">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <Heading
              title={t('text3712')}
              className={{ container: 'w-full sm:max-w-[500px]', title: 'text-balance' }}
            />
            <Paragraph className="w-full sm:max-w-[539px]" color="muted-foreground">
              {t('text3713')}
            </Paragraph>
          </div>
          <div className="flex flex-col items-start justify-start gap-3 sm:gap-4">
            <a
              href={`tel:${generalLayout?.contact?.phone?.replace(/\s+/g, '')}`}
              className="flex items-center justify-start gap-3"
            >
              <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-[4px] bg-white">
                <MdOutlinePhoneInTalk size={20} className="text-primary" />
              </div>
              <Heading
                tag="h3"
                title={generalLayout?.contact?.phone ?? ''}
                className={{ title: 'text-primary' }}
              />
            </a>
            <a
              href={`mailto:${generalLayout?.contact?.email}`}
              className="flex items-center justify-start gap-3"
            >
              <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-[4px] bg-white">
                <MdOutlineEmail size={20} className="text-primary" />
              </div>
              <Heading
                tag="h3"
                title={generalLayout?.contact?.email ?? ''}
                className={{ title: 'text-primary' }}
              />
            </a>
          </div>
        </div>
        <div className="w-full max-w-[652px]">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}
